import * as React from 'react';
import { difference } from 'lodash';
import { useMutation } from '@apollo/client';

import CreatableSelect from 'react-select/creatable';

import * as CreateTag from '../../core/gql/mutations/create-tag';
import * as DeleteTag from '../../core/gql/mutations/delete-tag';
import * as QueryTags from '../../core/gql/queries/tags';

import { GqlTag } from '../../core/gql/fragments/tag';
import { TagType, unarchivedOrgTagsForType } from '../../models/tag';
import { reactiveFeedbackMessage } from '../../core/apollo-cache';

const OrgTagInfo = {
  [TagType.AREA_OF_OPERATION]: {
    label: 'Areas of Operation',
    placeholder: 'Type an area of operation...',
    helpText: '',
    defaultOptions: [{ label: 'Primary', value: 'Primary' }],
    requiresOne: true,
  },
  [TagType.AGE_DIVISION]: {
    label: 'Age Divisions',
    placeholder: 'Select or type an age division...',
    helpText: '',
    defaultOptions: [
      { label: 'U6', value: 'U6' },
      { label: 'U7', value: 'U7' },
      { label: 'U8', value: 'U8' },
      { label: 'U9', value: 'U9' },
      { label: 'U10', value: 'U10' },
      { label: 'U11', value: 'U11' },
      { label: 'U12', value: 'U12' },
      { label: 'U13', value: 'U13' },
      { label: 'U14', value: 'U14' },
      { label: 'U15', value: 'U15' },
      { label: 'U16', value: 'U16' },
      { label: 'U17', value: 'U17' },
      { label: 'U18', value: 'U18' },
      { label: '6U', value: '6U' },
      { label: '7U', value: '7U' },
      { label: '8U', value: '8U' },
      { label: '9U', value: '9U' },
      { label: '10U', value: '10U' },
      { label: '11U', value: '11U' },
      { label: '12U', value: '12U' },
      { label: '13U', value: '13U' },
      { label: '14U', value: '14U' },
      { label: '15U', value: '15U' },
      { label: '16U', value: '16U' },
      { label: '17U', value: '17U' },
      { label: '18U', value: '18U' },
    ],
    requiresOne: false,
  },
  [TagType.COMPETITION_GENDER]: {
    label: 'Competition Genders',
    placeholder: 'Select or type a competition gender...',
    helpText: '',
    defaultOptions: [
      { label: 'Girls', value: 'Girls' },
      { label: 'Boys', value: 'Boys' },
      { label: 'Coed', value: 'Coed' },
      { label: 'Women', value: 'Women' },
      { label: 'Men', value: 'Men' },
    ],
    requiresOne: false,
  },
  [TagType.COMPETITION_LEVEL]: {
    label: 'Competition Levels',
    placeholder: 'Select or type a competition level...',
    helpText: '',
    defaultOptions: [
      { label: 'AAA', value: 'AAA' },
      { label: 'AA', value: 'AA' },
      { label: 'A', value: 'A' },
      { label: 'B', value: 'B' },
      { label: 'C', value: 'C' },
      { label: 'Gold', value: 'Gold' },
      { label: 'Silver', value: 'Silver' },
      { label: 'Bronze', value: 'Bronze' },
    ],
    requiresOne: false,
  },
};

const components = {
  DropdownIndicator: null,
};

const buildTagOptionValue = (tag: GqlTag) => ({ label: tag.name, value: tag.id, tag: tag, isFixed: false });
interface OrgTagProps {
  orgId: number;
  orgTags: GqlTag[];
  tagTypeName: TagType;
}

const OrgTag: React.FunctionComponent<OrgTagProps> = ({ tagTypeName, orgId, orgTags }) => {
  const tagTypeData = OrgTagInfo[tagTypeName];
  let tagValues = unarchivedOrgTagsForType(orgTags, tagTypeName).map(buildTagOptionValue);

  if (tagTypeData.requiresOne && tagValues.length == 1) {
    tagValues = [{ ...tagValues[0], isFixed: true }];
  }

  const mutationOptions = {
    refetchQueries: [{ query: QueryTags.QUERY, variables: { organizationId: orgId } }],
  };
  const [createTag, createTagResponse] = useMutation(CreateTag.MUTATION, mutationOptions);
  const [deleteTag, deleteTagResponse] = useMutation(DeleteTag.MUTATION, mutationOptions);

  const [inputValue, setInputValue] = React.useState('');
  const [stateTagValues, setStateTagValues] = React.useState([]);

  React.useEffect(() => {
    setStateTagValues(tagValues);
  }, [orgTags]);

  const addNewTag = async (newValue: string) => {
    try {
      await createTag({
        variables: {
          organizationId: orgId,
          type: tagTypeName,
          name: newValue,
        },
      });
    } catch {
      setStateTagValues(tagValues);
      reactiveFeedbackMessage({
        message: `Failed to add \"${newValue}\" to ${tagTypeData.label}.`,
        isError: true,
        autoclose: true,
      });
    }
  };

  const removeTag = async (tagOption: any) => {
    try {
      await deleteTag({
        variables: {
          organizationId: orgId,
          id: tagOption.value,
        },
      });
    } catch {
      setStateTagValues(tagValues);
      reactiveFeedbackMessage({
        message: `Failed to remove \"${tagOption.tag.name}\" from ${tagTypeData.label}.`,
        isError: true,
        autoclose: true,
      });
    }
  };

  const handleChange = (newTagValues: any, { action, removedValue }) => {
    if (['remove-value', 'pop-value'].includes(action) && removedValue.isFixed) {
      return;
    }

    if (removedValue) {
      setStateTagValues(newTagValues);
      removeTag(removedValue);
    } else {
      const added = difference(newTagValues, stateTagValues);
      const addedTag = added && added.length === 1 && added[0];

      setStateTagValues(newTagValues);
      addNewTag(addedTag.label);
    }
  };

  const handleInputChange = (newValue: string) => {
    setInputValue(newValue);
  };

  const isValidNewOption = (inputValue, selectValue, selectOptions) => {
    if (
      inputValue.trim().length === 0 ||
      selectValue.find((option) => option.label.toLowerCase() === inputValue.trim().toLowerCase()) ||
      selectOptions.find((option) => option.label.toLowerCase() === inputValue.trim().toLowerCase())
    ) {
      return false;
    }
    return true;
  };

  const isSubmitting = createTagResponse.loading || deleteTagResponse.loading;

  const options = tagTypeData.defaultOptions?.filter(
    (option) => !stateTagValues?.find((tagOption) => tagOption.label === option.label)
  );

  const styles = {
    multiValueLabel: (base, state) => (state.data.isFixed ? { ...base, paddingRight: 6 } : base),
    multiValueRemove: (base, state) => (state.data.isFixed ? { ...base, display: 'none' } : base),
  };

  return (
    <div className="u-padBottomXl">
      <h3 className="u-padBottomSm">{tagTypeData.label}</h3>
      <div>{tagTypeData.helpText}</div>
      <CreatableSelect
        isDisabled={isSubmitting}
        isLoading={isSubmitting}
        components={components}
        isMulti={true}
        isClearable={false}
        styles={styles}
        placeholder={tagTypeData.placeholder}
        inputValue={inputValue}
        value={stateTagValues}
        onInputChange={handleInputChange}
        onChange={handleChange}
        createOptionPosition="last"
        options={options}
        noOptionsMessage={() => 'Type a new value'}
        isValidNewOption={isValidNewOption}
        id={`${tagTypeName}-container`}
        formatOptionLabel={({ label, value }) => <span id={`tag-${value}`}>{label}</span>}
      />
    </div>
  );
};

export default OrgTag;
